import { useCallback, useEffect } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import { performCmdbGetRequest } from '@atlassian/jira-servicedesk-cmdb-client/src/fetch.tsx';
import {
	getCmdbObjectSchemaByIdUrl,
	getCmdbObjectSchemaListUrl,
} from '@atlassian/jira-servicedesk-cmdb-rest-urls/src/common/constants/object-schema.tsx';
import type { WorkspaceId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';

type Response = {
	isLast: boolean;
	maxResults: number;
	startAt: number;
	total: number;
};

// For now we just check if there is at least 1 schema accessible to the user
export const getAccessUrl = (workspaceId: string) =>
	getCmdbObjectSchemaListUrl(workspaceId, { maxResults: 1, includeCounts: false });

export const getSchemaAccessUrl = (workspaceId: string, schemaId: string) =>
	getCmdbObjectSchemaByIdUrl(workspaceId, schemaId);

const transformResponse = (res: Response) => res.total > 0;

// Checks for permission by ensuring the current user has access to at least 1 object schema
export const useObjectSchemaPermissionCheck = (workspaceId: WorkspaceId) => {
	const fetchRequest = useCallback(
		() =>
			performCmdbGetRequest(getAccessUrl(String(workspaceId)))
				.then(transformResponse)
				.catch((err) => {
					fireCmdbErrorAnalytics({
						meta: {
							id: 'useObjectSchemaPermissionCheck',
							packageName: 'jiraServicedeskInsightCommonWorkspaceContext',
							teamName: 'falcons',
						},
						error: err,
						sendToPrivacyUnsafeSplunk: true,
					});
				}),
		[workspaceId],
	);

	// @ts-expect-error - TS2345 - Argument of type '() => Promise<boolean | void>' is not assignable to parameter of type '(requestData: undefined) => Promise<boolean | undefined>'.
	const { fetch, data, error, loading } = useService<boolean>(fetchRequest);

	useEffect(() => {
		fetch();
	}, [fetch]);

	return { data, error, loading };
};

// Checks for permission by ensuring the user has access to a specified object schema
export const useSpecificObjectSchemaPermissionCheck = (
	workspaceId: WorkspaceId,
	schemaId: string,
) => {
	const fetchRequest = useCallback(
		() =>
			performCmdbGetRequest(getSchemaAccessUrl(workspaceId, schemaId))
				.then(() => true)
				.catch((error) => {
					if (error.statusCode === 403) {
						// Only return false when a permission error specifically is returned
						return false;
					}

					// Otherwise log the error and return true for the permission check
					fireCmdbErrorAnalytics({
						meta: {
							id: 'useSpecificObjectSchemaPermissionCheck',
							packageName: 'jiraServicedeskInsightCommonWorkspaceContext',
							teamName: 'falcons',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
					return true;
				}),
		[workspaceId, schemaId],
	);

	const { fetch, data, error, loading } = useService<boolean>(fetchRequest);

	useEffect(() => {
		fetch();
	}, [fetch]);

	return { data, error, loading };
};
